import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { FiMenu } from 'react-icons/fi'
import { Flex, Box, tokens } from 'src/componentv2'
import { useBreakpoint } from 'src/componentv2/Base/hooks/useBreakpoint'
import { AppSidebar } from 'src/app/dashboardv2/layout/AppSidebar'
import { sidebarRouteConfig } from 'src/app/dashboardv2/layout/sidebarRouteConfig'

export interface SidebarOverride {
    // Some routes render either a full-bleed wizard screen or a sidebar
    // shell depending on page-internal state (e.g. onboarding vs already
    // configured) — pages toggle that with `visible`.
    visible?: boolean
    // A few routes (e.g. profile) show a different nav set depending on data
    // only known once the page has loaded (the signed-in user's role).
    variant?: 1 | null
}

const SidebarOverrideContext = createContext<(override: SidebarOverride | null) => void>(() => {})

export const useSidebarOverride = (override: SidebarOverride | null) => {
    const setOverride = useContext(SidebarOverrideContext)
    const { visible, variant } = override ?? {}
    useEffect(() => {
        setOverride(override ? { visible, variant } : null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, variant])
}

export const DashboardLayout = () => {
    const location = useLocation()
    const routeKey = location.pathname.replace(/^\/?dashboardv2\/?/, '').split('/')[0]
    const entry = sidebarRouteConfig[routeKey]

    const [override, setOverride] = useState<SidebarOverride | null>(null)
    const [mobileNavOpen, setMobileNavOpen] = useState(false)
    const breakpoint = useBreakpoint()
    const isMobileOrTablet = breakpoint === 'mobile' || breakpoint === 'tablet'

    // Reset any override left over from the previous route during render
    // (not in an effect) so it can't race with — and get clobbered by — the
    // next page's own useSidebarOverride mount effect in the same commit.
    const lastRouteKeyRef = useRef(routeKey)
    if (lastRouteKeyRef.current !== routeKey) {
        lastRouteKeyRef.current = routeKey
        if (override !== null) setOverride(null)
        if (mobileNavOpen) setMobileNavOpen(false)
    }

    const visible = override?.visible ?? !!entry
    const variant = override?.variant ?? entry?.variant ?? null
    const showSidebar = visible && !!entry

    // NOTE: <Outlet/> must stay at the exact same position in the tree
    // (same parent, same depth) across every branch of this render.
    // Previously this component had two separate `return` statements —
    // one where <Outlet/> was a direct child of the Provider, and one
    // where it was nested inside <Flex><Box>. Toggling `visible` (e.g.
    // from a page's useSidebarOverride) therefore changed Outlet's
    // position in the tree, and React unmounts/remounts a subtree when
    // its position or structure changes. That silently remounted every
    // routed page on each visibility toggle — resetting all its state
    // (e.g. a checklist-loaded flag) back to defaults, which retriggered
    // the same override change, causing an infinite remount/flicker loop.
    // Keeping a single, structurally-stable return fixes this at the root.
    return (
        <SidebarOverrideContext.Provider value={setOverride}>
            <Flex items="stretch" width="100%" style={{ minHeight: '100vh' }}>
                {showSidebar && (
                    <AppSidebar
                        activeKey={entry.activeKey}
                        variant={variant}
                        mobileOpen={isMobileOrTablet && mobileNavOpen}
                        onMobileClose={() => setMobileNavOpen(false)}
                    />
                )}
                {showSidebar && isMobileOrTablet && (
                    <button
                        type="button"
                        aria-label="Open menu"
                        onClick={() => setMobileNavOpen(true)}
                        style={{
                            position: 'fixed',
                            top: '1.6rem',
                            left: '1.6rem',
                            zIndex: 999,
                            width: '4rem',
                            height: '4rem',
                            borderRadius: '0.8rem',
                            border: 'none',
                            background: tokens.white,
                            boxShadow: tokens.shadowCard,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <FiMenu size={20} />
                    </button>
                )}
                <Box bgColor={showSidebar ? tokens.pageBg : undefined} width="100%">
                    <Outlet />
                </Box>
            </Flex>
        </SidebarOverrideContext.Provider>
    )
}

export default DashboardLayout